import { useRef, useState } from 'react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command'
import { Badge } from './badge'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import Card from 'app/frontend/base/components/Card'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

interface Props<T> {
  options: { label: React.ReactNode; value: T; searchValue?: string }[]
  value: T[]
  placeholder?: string
  onChange: (value: T[]) => void
  className?: string
  selectedDisplay?: 'badges' | 'count'
  inModal?: boolean
  optionWidth?: 'matchTrigger' | 'fit'
  isSearchable?: boolean
}

export function MultiSelect<T>({
  options,
  value,
  placeholder,
  onChange,
  className,
  selectedDisplay = 'badges',
  inModal = false,
  optionWidth = 'matchTrigger',
  isSearchable = false,
}: Props<T>) {
  const triggerRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [side, setSide] = useState<'top' | 'bottom'>('bottom')

  const selected = options.filter((option) => value.includes(option.value))

  const handleUnselect = (option: Props<T>['options'][number]) => {
    onChange(value.filter((s) => s !== option.value))
  }

  const selectables = options.filter((option) => !value.includes(option.value))
  const isCountDisplay = selectedDisplay === 'count'
  const visibleOptions = isCountDisplay ? options : selectables

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next && triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect()
          const spaceBelow = window.innerHeight - rect.bottom
          const spaceAbove = rect.top
          setSide(
            spaceBelow < 340 && spaceAbove > spaceBelow ? 'top' : 'bottom',
          )
        }
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          role='combobox'
          aria-expanded={open}
          tabIndex={0}
          className={cn('w-full min-h-10 cursor-pointer', className)}
          onKeyDown={(event) => {
            if (event.key !== 'Backspace' && event.key !== 'Delete') return
            if (value.length === 0) return
            const next = [...value]
            next.pop()
            onChange(next)
          }}
        >
          <Card className='group cursor-pointer text-sm' size='sm'>
            <div
              className={cn(
                'flex items-center gap-1',
                isCountDisplay ? 'flex-nowrap' : 'flex-wrap',
              )}
            >
              <div
                className={cn(
                  'flex min-w-0 flex-1 gap-1',
                  isCountDisplay ? 'items-center flex-nowrap' : 'flex-wrap',
                )}
              >
                {isCountDisplay && selected.length > 0 ? (
                  <span className='ml-2 text-foreground whitespace-nowrap'>
                    {selected.length === 1
                      ? selected[0].label
                      : `${selected.length} selected`}
                  </span>
                ) : null}
                {isCountDisplay
                  ? null
                  : selected.map((option) => (
                      <Badge
                        key={option.value as unknown as string}
                        variant='outline'
                      >
                        {option.label}
                        <button
                          type='button'
                          className='ml-1 rounded-full outline-hidden focus:ring-1 focus:ring-ring'
                          onPointerDown={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            handleUnselect(option)
                          }}
                        >
                          <X className='h-3 w-3 text-muted-foreground hover:text-foreground' />
                        </button>
                      </Badge>
                    ))}
                {selected.length === 0 ? (
                  <span className='ml-2 text-muted-foreground'>
                    {placeholder ?? 'Select options...'}
                  </span>
                ) : null}
                {isCountDisplay && selected.length > 0 ? (
                  <button
                    type='button'
                    className='ml-auto rounded-full outline-hidden focus:ring-1 focus:ring-ring'
                    onPointerDown={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      onChange([])
                    }}
                  >
                    <X className='opacity-50 hover:opacity-100 transition-opacity p-1' />
                  </button>
                ) : null}
              </div>
              <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 text-muted-foreground' />
            </div>
          </Card>
        </div>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        side={side}
        avoidCollisions={false}
        className={cn(
          'p-0 pointer-events-auto',
          optionWidth === 'fit' ? 'w-fit' : 'w-(--radix-popover-trigger-width)',
          inModal && 'z-60',
        )}
        onWheel={(event) => event.stopPropagation()}
        onInteractOutside={(event) => {
          if (
            event.target instanceof Node &&
            triggerRef.current?.contains(event.target)
          ) {
            event.preventDefault()
          }
        }}
      >
        <Command
          filter={(itemValue, search) =>
            itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }
        >
          {isSearchable ? <CommandInput placeholder='Search' /> : null}
          <CommandList className='h-auto'>
            <CommandEmpty>No results</CommandEmpty>
            <CommandGroup>
              {visibleOptions.map((option) => {
                const selectedOption = value.includes(option.value)
                return (
                  <CommandItem
                    key={option.value as unknown as string}
                    value={
                      option.searchValue ??
                      (typeof option.label === 'string'
                        ? option.label
                        : String(option.value))
                    }
                    onSelect={() => {
                      if (selectedOption) {
                        handleUnselect(option)
                      } else {
                        onChange([...value, option.value])
                      }
                    }}
                    className='cursor-pointer'
                  >
                    <div className='flex w-full items-center justify-between gap-2'>
                      <span>{option.label}</span>
                      {selectedOption ? <Check className='h-4 w-4' /> : null}
                    </div>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
